import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { retryErroredEncounters, type GetErroredEncountersOptions, type RetryResult } from "@/api/writeBackManagement";

function countedEncounters(count: number, label: string): string {
  return `${count} ${label} encounter${count === 1 ? "" : "s"}`;
}

function summarizeRetryResult({ retried, unchanged, failed }: RetryResult): string {
  const parts = [
    retried > 0 ? countedEncounters(retried, "retried") : null,
    unchanged > 0 ? countedEncounters(unchanged, "unchanged") : null,
    failed > 0 ? countedEncounters(failed, "failed") : null,
  ].filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(", ") : "Nothing to retry";
}

export function useRetryErroredWriteBacks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options?: GetErroredEncountersOptions) => retryErroredEncounters(options),
    onSuccess: (data: RetryResult) => {
      const summary = summarizeRetryResult(data);
      if (data.failed > 0) {
        toast.warning(summary);
      } else if (data.retried === 0 && data.unchanged > 0) {
        toast.info(summary);
      } else {
        toast.success(summary);
      }
      queryClient.invalidateQueries({ queryKey: ["errored-write-backs"] });
    },
    onError: (error) => {
      toast.error(`Failed to retry: ${error.message}`);
    },
  });
}
